import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

import { LicenseInfo } from "@mui/x-license";
import * as Sentry from "@sentry/react";
import { isAuthError } from "@supabase/auth-js";
import { supabaseIntegration } from "@supabase/sentry-js-integration";
import { SupabaseClient } from "@supabase/supabase-js";
import React from "react";
import ReactDOM from "react-dom/client";

import { App } from "./App";
import { config } from "./config";

const IGNORED_ERROR_CODES = new Set([
  "session_not_found",
  "session_expired",
  "refresh_token_not_found",
  "refresh_token_already_used"
]);

const shouldIgnoreError = (exception: unknown): boolean => {
  if (!isAuthError(exception)) {
    return false;
  }
  const code = (exception as { code?: string }).code;
  if (code && IGNORED_ERROR_CODES.has(code)) {
    return true;
  }
  return exception.name === "AuthSessionMissingError";
};

if (config.sentryDsn) {
  Sentry.init({
    dsn: config.sentryDsn,
    sendDefaultPii: true,
    integrations: [
      Sentry.captureConsoleIntegration({
        levels: ["error"]
      }),
      supabaseIntegration(SupabaseClient, Sentry, {
        tracing: true,
        breadcrumbs: true,
        errors: true
      }),
      Sentry.browserTracingIntegration({
        shouldCreateSpanForRequest: (url) => {
          return !url.startsWith(`${config.supabaseUrl}/rest`);
        }
      }),
      Sentry.replayIntegration()
    ],
    beforeSend: (event, hint) =>
      shouldIgnoreError(hint?.originalException) ? null : event,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0
  });
}

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
